import * as React from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";

interface SimpleDatePickerProps {
  selectedDate: Date | undefined;
  onDateChange: (date: Date | undefined | null) => void;
  className?: string;
  placeholder?: string;
  minToday?: boolean;
}

export function SimpleDatePicker({
  selectedDate,
  onDateChange,
  className,
  placeholder = "Pick a date",
  minToday
}: SimpleDatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [date, setDate] = React.useState<Date | undefined>(selectedDate);

  // Update local state when props change
  React.useEffect(() => {
    setDate(selectedDate);
  }, [selectedDate]);

  // Format the date display text
  const dateDisplayText = React.useMemo(() => {
    if (!date) {
      return placeholder;
    }
    
    return format(date, "LLL dd, y");
  }, [date, placeholder]);

  // Handle date selection
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = e.target.value ? new Date(e.target.value) : undefined;
    setDate(newDate);
    onDateChange(newDate);
    if (newDate) {
      setOpen(false); // Auto-close when date is selected
    }
  };

  const formatDateForInput = (date?: Date) => {
    return date ? format(date, "yyyy-MM-dd") : "";
  };

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant={"outline"}
            className={cn(
              "w-full justify-start text-left font-normal",
              !date && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            <span>{dateDisplayText}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent 
          className="w-auto p-4 space-y-4 bg-white"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <div className="space-y-2">
            <label htmlFor="date" className="text-sm font-medium">
              Select Date
            </label>
            <input
  id="date"
  type="date"
  className="w-full p-2 border rounded-md bg-white"
  value={formatDateForInput(date)}
  onChange={handleDateChange}
  min={minToday ? formatDateForInput(new Date()) : undefined} // ← add logic here
/>
          </div>
          <div className="flex justify-end">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => {
                onDateChange(undefined);
                setDate(undefined);
                setOpen(false);
              }}
              className="mr-2"
            >
              Clear
            </Button>
            <Button 
              size="sm" 
              onClick={() => setOpen(false)}
            >
              Apply
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default SimpleDatePicker;